<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">
        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('specialOrderPositionsLabel') }}</div>
          </template>
          <template #end>
            <div class="p-d-flex p-flex-wrap">
              <div class="p-d-flex p-ai-center p-mr-2">
                <Checkbox @change="changeRequestFilters"
                          :disabled="dataTableIsLoading"
                          id="special-order-position-show-not-ordered-checkbox"
                          :binary="true"
                          v-model="showNotOrdered"/>
                <div>
                  <label for="special-order-position-show-not-ordered-checkbox">
                    <div class="pointer p-p-1">{{ $t('showNotOrderedLabel') }}</div>
                  </label>
                </div>
              </div>

              <div class="p-d-flex p-ai-center p-mr-2" v-if="canUsePurchaseInvoices && $store.getters.isAccountantOrHigher">
<!--                <Checkbox @change="getItems(true)"-->
                <Checkbox @change="changeRequestFilters"
                          :disabled="dataTableIsLoading"
                          id="special-order-position-show-only-not-fully-invoiced-checkbox"
                          :binary="true"
                          v-model="showOnlyNotFullyInvoiced"/>
                <div>
                  <label for="special-order-position-show-only-not-fully-invoiced-checkbox">
                    <div class="pointer p-p-1">{{ $t('showOnlyNotFullyInvoicedLabel') }}</div>
                  </label>
                </div>
              </div>
              
              <CustomDropdown id="supplier"
                              class="p-mr-2"
                              style="min-width: 180px; max-width: 200px;"
                              :disabled="dataTableIsLoading"
                              ref="selectedSupplierDropdown"
                              :searchData="supplierSearchData"
                              v-model="selectedSupplier"
                              data-key="id"
                              :options="suppliersData"
                              :filterPlaceholder="$t('lineItemSearchSupplierPlaceholder')"
                              optionLabel="company_name"
                              :filter="true"
                              :filterFields="['name', 'company_name', 'reg_number']"
                              :loading="suppliersDataIsLoading"
                              @change="changeSupplier"
                              @filter="searchSuppliers"
                              :showClear="true">
                <template #value="slotProps">
                  <div v-if="slotProps.value">
                    <div>{{ slotProps.value.company_name }} <span v-if="slotProps.value.name">({{ slotProps.value.name }})</span></div>
                  </div>
                  <span v-else>{{ $t('lineItemSelectSupplierPlaceholder') }}</span>
                </template>
                <template #option="slotProps">
                  <div v-if="slotProps.option">
                    <div>{{ slotProps.option.company_name }} <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></div>
                  </div>
                </template>
              </CustomDropdown>
              <SearchInput :disabled="dataTableIsLoading"
                           @change-processed-search-value="changeProcessedSearchValue"
                           v-model="searchData"
                           @show-spinner="isLoading = true"/>
            </div>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :dataTableIsLoading="dataTableIsLoading"
                          :showHeader="false"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
                            {name: 'code', title: $t('Code'), sortable: false},
                            {name: 'name', title: $t('Name'), sortable: false},
                            {name: 'supplier', title: $t('Supplier'), sortable: false},
                            {name: 'qty', title: $t('Qty'), minWidth: '70px', sortable: false},
                            {name: 'ordered_qty', title: $t('orderedQtyLabel'), minWidth: '70px', sortable: false},
                            {name: 'reduced_qty', title: $t('returnedQtyLabel'), minWidth: '70px', sortable: false},
                            {name: 'purchase_price', title: $t('purchasePriceShortLabel'), minWidth: '70px', sortable: false},
                            {name: 'sell_price', title: $t('sellingPriceShortLabel'), minWidth: '70px', sortable: false},
                            // {name: 'order', class: 'ti-hummer', width: '40px', sortable: false},
                            {name: 'order', width: '40px', sortable: false},
                            {name: 'car', title: $store.getters.isForkliftTenant ? $t('Equipment') : $t('Car'), width: '15%', sortable: false},
                            {name: '', title: null, sortable: false},
                            {name: '', title: null, sortable: false},
                            // {name: 'fixed_qty', title: null, class: 'ti-link', sortable: false},
                            {name: 'fixed_qty', title: null, sortable: false},
                            {name: 'created_at', title: $t('Created'), sortable: false},
                          ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span v-if="item.code">{{ item.code }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>{{ item.name }}</td>
              <td>
                <span v-if="item.supplier">{{ item.supplier.company_name }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>{{ formatDecimal(item.qty) }}</td>
              <td>{{ formatDecimal(item.ordered_qty) }}</td>
              <td>{{ formatDecimal(item.reduced_qty) }}</td>
              <td :class="{'red-soft-background' : checkSellingPriceIsTooLow(item)}">
                <span v-if="item.purchase_price">{{ formatCurrency(item.purchase_price, 2, 4) }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td :class="{'red-soft-background warning-color' : checkSellingPriceIsTooLow(item)}">
                <span v-if="item.sell_price">{{ formatCurrency(item.sell_price, 2, 4) }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
<!--                <span v-if="item.order?.uid" class="p-d-flex p-ai-center">-->
<!--                  <router-link :to="{ path: `/orders/${item.order.uid}` }" target="_blank" draggable="false">-->
<!--                    <Button class="p-button-text p-p-1" v-tooltip.top="item.order?.number ? item.order.number : null">-->
<!--                      <i class="ti-hummer"/>-->
<!--                    </Button>-->
<!--                  </router-link>-->
<!--                </span>-->
<!--                <i v-else class="ti-minus"/>-->
                <OpenOrderButton :position="item"/>
              </td>
              <td>
                <span v-if="item.car?.uid">
                  <router-link :to="{ path: `/vehicles/${item.car.uid}` }"
                               :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                               rel="noopener"
                               draggable="false">
                    <i :class="!$store.getters.isForkliftTenant ? 'ti-car p-mr-1' : ''"></i>
                    <span>{{ formatCarLabel(item.car) }}</span>
                  </router-link>
                </span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <RequestPositionStateButton :position="item" :orderId="item.order_id" :isDisabled="true"/>
              </td>
              <td>
                <PositionHistoryButton :position="item"/>
              </td>
              <td>
                <PositionFixedQtyButton :position="item" fixedMode="request"/>
              </td>
              <td class="table-date-line">
                {{ formatBy(item.created_at, item.creator) }}
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

			</div>
		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import httpMixins from "@/mixins/httpMixins";
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import formatMixins from "@/mixins/formatMixins";
import PositionHistoryButton from "@/components/PositionHistoryButton";
import RequestPositionStateButton from "@/pages/orders/components/RequestPositionStateButton";
import PositionFixedQtyButton from "@/components/PositionFixedQtyButton";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import OpenOrderButton from "@/components/OpenOrderButton.vue";

export default {
  mixins: [ httpMixins, datatableMixins, formatMixins, showErrorsMixins, billingFeaturesMixins ],
  components: {OpenOrderButton, RequestPositionStateButton, PositionHistoryButton, PositionFixedQtyButton },
	data() {
		return {
      showNotOrdered: true,
      showOnlyNotFullyInvoiced: false,
      suppliers: null,
      fieldsExpand: 'order,car,historyCount,requestStateHistoryCount',
      apiLink: 'order-position',
      routePath: '/special-order-items',
      sort: {
        column: 'id',
        direction: '-',
      },
		}
	},
  watch: {
    suppliers(value) {
      if (value?.length) {
        this.suppliersData = [...value]
      } else {
        this.suppliersData = null
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    // if (!this.$store.state.user?.role) return
    this.getPageData()
	},
  methods: {
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.getItems()
      this.getSuppliers().then(() => this.checkSupplierInArray())
      // this.getModalData().then(() => this.checkSupplierInArray())
    },
    // checkSellingPriceIsTooLow(item) {
    //   const sellPrice = item.sell_price ? +item.sell_price : 0
    //   const purchasePrice = item.purchase_price ? +item.purchase_price : 0
    //   return sellPrice < purchasePrice
    // },
    checkSellingPriceIsTooLow(item) {
      const sellPrice = Number(item.sell_price || 0)
      const purchasePrice = Number(item.purchase_price || 0)

      return sellPrice > 0 && purchasePrice > 0 && sellPrice < purchasePrice
    },
    // changeRequestFilters() {
    //   // this.pagination.currentPage = 1
    //   this.getItems()
    // },
    async getItems() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('requests')
      try {
        const { status, data, headers } = await httpClient(`${this.apiLink}`, {
          params: {
            supplierId: this.selectedSupplierId,
            page: this.pagination.currentPage,
            show_only_unfixed: +(this.canUsePurchaseInvoices && this.showOnlyNotFullyInvoiced),
            show_not_ordered: +this.showNotOrdered,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand
          },
          signal: ctrl?.signal,
        })

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.items = []
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.requests === ctrl) {
          this._abortors.requests = null
        }
      }
    },
	},
  computed: {
    actionButtonsCount() {
      return 0
    },
  }
}
</script>

<style scoped lang="scss">

</style>
